// Catalogue des événements notifiables (H8-02) : code, libellé, module, destinataires, canaux
// autorisés, canaux imposés (non désactivables par les préférences) et canaux par défaut.
import { z } from 'zod';

export const CANAUX_NOTIFICATION = ['application', 'portail', 'courriel', 'sms', 'pwa'] as const;
export type CanalNotification = (typeof CANAUX_NOTIFICATION)[number];

export const ETATS_NOTIFICATION = ['en_attente', 'envoye', 'remis', 'echec', 'abandonne'] as const;
export type EtatNotification = (typeof ETATS_NOTIFICATION)[number];

/** Canaux « table » : la ligne de notification est la remise elle-même. */
export const CANAUX_INTERNES: readonly CanalNotification[] = ['application', 'portail'];

/** Délais des reprises après échec d'un envoi externe (minutes) : trois reprises puis abandon. */
export const DELAIS_REPRISE_NOTIFICATION_MINUTES = [1, 5, 30] as const;

export interface EvenementNotifiable {
  libelle: string;
  module: string;
  destinataires: 'agent' | 'usager';
  canauxAutorises: readonly CanalNotification[];
  canauxImposes: readonly CanalNotification[];
  canauxParDefaut: readonly CanalNotification[];
}

const AGENT_TOUS = ['application', 'courriel', 'sms', 'pwa'] as const;
const USAGER_TOUS = ['portail', 'courriel', 'sms', 'pwa'] as const;

export const CATALOGUE_NOTIFICATIONS = {
  'dossier.affecte': {
    libelle: 'Dossier qui vous est affecté',
    module: 'M05',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application', 'courriel'],
  },
  'dossier.echeance_proche': {
    libelle: 'Échéance interne d\'un dossier demain',
    module: 'M05',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'dossier.depose': {
    libelle: 'Accusé de dépôt de votre dossier',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'courriel'],
  },
  'dossier.complement_demande': {
    libelle: 'Pièce ou information complémentaire demandée',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'courriel', 'sms'],
  },
  'dossier.decision': {
    libelle: 'Décision sur votre dossier',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail', 'courriel'],
    canauxParDefaut: ['portail', 'courriel', 'sms'],
  },
  'dossier.relance_complement': {
    libelle: 'Rappel du complément attendu (relance)',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'courriel', 'sms'],
  },
  'dossier.complement_fourni': {
    libelle: 'Complément fourni par l\'usager sur un dossier de votre service',
    module: 'M05',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'message.usager': {
    libelle: 'Nouveau message de la collectivité sur votre dossier',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'courriel', 'sms'],
  },
  'message.agent': {
    libelle: 'Nouveau message sur la conversation d\'un dossier de votre service',
    module: 'M05',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'representation.validee': {
    libelle: 'Représentation validée par la collectivité',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'courriel'],
  },
  'representation.refusee': {
    libelle: 'Représentation refusée par la collectivité',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'courriel'],
  },
  'rendez_vous.confirme': {
    libelle: 'Rendez-vous confirmé',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'sms', 'courriel'],
  },
  'rendez_vous.rappel': {
    libelle: 'Rappel de rendez-vous la veille',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'sms'],
  },
  'rendez_vous.reporte': {
    libelle: 'Rendez-vous reporté par la collectivité',
    module: 'M04',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'sms', 'courriel'],
  },
  'reclamation.repondue': {
    libelle: 'Réponse à votre réclamation',
    module: 'M22',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'courriel', 'sms'],
  },
  // Recettes (lot L2-A).
  'recette.quittance_emise': {
    libelle: 'Quittance de votre paiement',
    module: 'M07',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'sms'],
  },
  'recette.paiement_refuse': {
    libelle: 'Paiement refusé par le prestataire',
    module: 'M07',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'sms'],
  },
  'recette.remboursement_ordonne': {
    libelle: 'Remboursement ordonné par la collectivité',
    module: 'M07',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'sms', 'courriel'],
  },
  'recette.creance_echue': {
    libelle: 'Rappel d\'une somme due après l\'échéance',
    module: 'M07',
    destinataires: 'usager',
    canauxAutorises: USAGER_TOUS,
    canauxImposes: ['portail'],
    canauxParDefaut: ['portail', 'sms'],
  },
  // Interventions et signalements (lot L3-A, D-042) : usagers demandeurs, agents de terrain, responsable technique.
  'signalement.qualifie': { libelle: 'Signalement pris en compte', module: 'M12', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'sms'] },
  'signalement.oriente': { libelle: 'Signalement transmis à un organisme compétent', module: 'M12', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'sms'] },
  'signalement.rejete': { libelle: 'Signalement non retenu', module: 'M12', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'sms'] },
  'intervention.planifiee': { libelle: 'Intervention planifiée pour votre signalement', module: 'M12', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'sms'] },
  'intervention.cloturee': { libelle: 'Intervention terminée pour votre signalement', module: 'M12', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'sms', 'courriel'] },
  'intervention.annulee': { libelle: 'Intervention annulée pour votre signalement', module: 'M12', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'sms'] },
  'mission.affectee': { libelle: 'Mission de terrain qui vous est affectée ou modifiée', module: 'M12', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application', 'pwa'] },
  'mission.conflit': { libelle: 'Conflit de synchronisation à trancher', module: 'M12', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application', 'courriel'] },
  'courrier.affecte': {
    libelle: 'Courrier affecté à votre service',
    module: 'M02',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'courrier.echeance_proche': {
    libelle: 'Échéance interne d\'un courrier demain',
    module: 'M02',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'courrier.envoye': {
    libelle: 'Courrier départ que vous avez rédigé envoyé',
    module: 'M02',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'document.a_signer': {
    libelle: 'Document en attente de votre signature',
    module: 'M03',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application', 'pwa'],
  },
  'assemblee.convocation': {
    libelle: 'Convocation à une session',
    module: 'M16',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application', 'courriel'],
    canauxParDefaut: ['application', 'courriel', 'sms'],
  },
  'assemblee.pv_a_valider': { libelle: 'Procès-verbal de séance à valider', module: 'M16', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application'] },
  'notification.abandon': {
    libelle: 'Envoi de notification abandonné après trois reprises',
    module: 'M25',
    destinataires: 'agent',
    canauxAutorises: ['application'],
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'systeme.essai': {
    libelle: 'Notification d\'essai envoyée par un administrateur',
    module: 'M25',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: [],
    canauxParDefaut: ['application'],
  },
  // Déchets et propreté, inspections et contrôles (lot L3-B) : destinataires agents.
  'tournee.non_realisee': {
    libelle: 'Tournée de collecte non réalisée',
    module: 'M13',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application', 'courriel'],
  },
  'constat.transmis': {
    libelle: 'Constat transmis à l\'autorité',
    module: 'M23',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application', 'courriel'],
  },
  'constat.ecart_domaine_public': {
    libelle: 'Écart constaté sur une autorisation du domaine public',
    module: 'M23',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  'mesure.echue': {
    libelle: 'Mesure corrective échue sans réalisation',
    module: 'M23',
    destinataires: 'agent',
    canauxAutorises: AGENT_TOUS,
    canauxImposes: ['application'],
    canauxParDefaut: ['application'],
  },
  // Lot L4-B : chaîne de la dépense (M06).
  'depense.besoin_a_valider': { libelle: 'Besoin de votre service à valider', module: 'M06', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application'] },
  'depense.engagement_a_ordonnancer': { libelle: 'Dépense liquidée à ordonnancer', module: 'M06', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application', 'courriel'] },
  'depense.paiement_confirme': { libelle: 'Paiement d\'une dépense de votre service confirmé', module: 'M06', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application'] },
  // Lot L4-C : achats (M15) et chantiers (M14).
  'achat.offre_recue': { libelle: 'Offre reçue pour une consultation', module: 'M15', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application'] },
  'achat.contrat_signe': { libelle: 'Contrat signé', module: 'M15', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application', 'courriel'] },
  'chantier.jalon_en_retard': { libelle: 'Jalon de chantier en retard', module: 'M14', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application', 'courriel'] },
  'chantier.reception': { libelle: 'Réception de travaux prononcée', module: 'M14', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application'] },
  // Lot L4-A : réservations d'équipements (M19, usager) et sortie d'un bien du patrimoine (M10, maire ou président).
  'reservation.a_payer': { libelle: 'Réservation acceptée, paiement attendu', module: 'M19', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'courriel', 'sms'] },
  'reservation.confirmee': { libelle: 'Réservation d\'un équipement confirmée', module: 'M19', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'courriel', 'sms'] },
  'reservation.refusee': { libelle: 'Réservation d\'un équipement refusée', module: 'M19', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'courriel'] },
  'reservation.rappel': { libelle: 'Rappel de réservation la veille', module: 'M19', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: [], canauxParDefaut: ['portail', 'sms'] },
  'bien.sortie_a_valider': { libelle: 'Sortie d\'un bien du patrimoine à valider', module: 'M10', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application', 'courriel'] },
  'personnel.contrat_echeance': { libelle: 'Contrat d\'un agent arrivant à échéance sous 60 jours', module: 'M11', destinataires: 'agent', canauxAutorises: AGENT_TOUS, canauxImposes: ['application'], canauxParDefaut: ['application', 'courriel'] }, // Lot L5-A.
  'quartier.designation': { libelle: 'Désignation comme représentant d\'un quartier', module: 'M17', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail', 'sms'] }, // Lot L5-C1.
  'quartier.besoin_suivi': { libelle: 'Suite donnée à un besoin exprimé pour le quartier', module: 'M17', destinataires: 'usager', canauxAutorises: USAGER_TOUS, canauxImposes: ['portail'], canauxParDefaut: ['portail'] }, // Lot L5-C1.
} as const satisfies Record<string, EvenementNotifiable>;

export type CodeEvenementNotifiable = keyof typeof CATALOGUE_NOTIFICATIONS;
export const CODES_EVENEMENTS_NOTIFIABLES = Object.keys(CATALOGUE_NOTIFICATIONS) as CodeEvenementNotifiable[];

export function evenementNotifiable(code: string): EvenementNotifiable | undefined {
  return (CATALOGUE_NOTIFICATIONS as Record<string, EvenementNotifiable>)[code];
}

const schemaCanal = z.enum(CANAUX_NOTIFICATION);

/** Préférences : par événement, canal activé ou non. Les canaux imposés restent toujours actifs. */
export const schemaPreferencesNotifications = z.object({
  preferences: z.partialRecord(z.enum(CODES_EVENEMENTS_NOTIFIABLES as [CodeEvenementNotifiable, ...CodeEvenementNotifiable[]]), z.partialRecord(schemaCanal, z.boolean())),
});
export type PreferencesNotifications = z.infer<typeof schemaPreferencesNotifications>['preferences'];

export const schemaFiltreNotifications = z.object({
  non_lues: z.enum(['oui', 'non']).default('non'),
  limite: z.coerce.number().int().min(1).max(200).default(50),
});

export const schemaFiltreJournalNotifications = z.object({
  etat: z.enum(ETATS_NOTIFICATION).optional(),
  canal: schemaCanal.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

export const schemaEssaiNotification = z.object({
  canaux: z.array(schemaCanal).min(1).max(5),
  message: z.string().trim().min(1).max(500).default('Notification d\'essai'),
});

export const schemaAbonnementPush = z.object({
  endpoint: z.string().url().startsWith('https://', 'Point de terminaison HTTPS attendu').max(1000),
  keys: z.object({ p256dh: z.string().min(10).max(200), auth: z.string().min(8).max(100) }),
});
